/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

/**
 * Change (Reznak): added optional parameter aParameters
 *
 * @param aRenderGraph
 * @param aParameters
 * @constructor
 */
import {logException} from "App/debug";
import {vec2, vec3, vec4} from "gl-matrix";
import {unProject} from "App/utils/glu";
import WebGLContext from "App/web_gl_context";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import View from "App/view/view";
import ScreenFrameBuffer from "App/render/webgl/utils/screen_frame_buffer";
import MRTFrameBuffer from "App/render/webgl/utils/mrt_frame_buffer";
import OffscreenFrameBuffer from "App/render/webgl/utils/offscreen_frame_buffer";

export class Framebuffer {

    private _renderGraph: WebGLRenderGraph;
    private _context: WebGLContext;
    private parameters: any;
    private renderable: ScreenFrameBuffer | MRTFrameBuffer | OffscreenFrameBuffer;
    private validContent: boolean;
    private renderPassTypeName: string;
    private _pgc: any;

    constructor(aRenderGraph: WebGLRenderGraph, aParameters: any)
    {
        this._renderGraph = aRenderGraph;
        this._context = <WebGLContext>aRenderGraph.getContext();

        this.parameters = {
            size: vec2.create(), // already with scaled applied
            scale: 1.0
        };

        this.renderable = null;
        this.validContent = false;

        this.renderPassTypeName = "";

        if (aParameters) {
            this.setParameters(aParameters);
        }

        this._pgc = {
            windowPoint: vec4.create(),
            pickedPoint: vec3.create(),
            zoomToPoint: vec3.create()
        };
    }

    public getWebGLContext(): WebGLContext {
        return this._context;
    }

    public release(): void
    {
        if (this.renderable) {
            this.renderable.release();
        }

        this.renderable = null;
        this.validContent = false;
    }

    public getSize(): vec2
    {
        return this.parameters.size;
    }

    public getScale(): number
    {
        return this.parameters.scale;
    }

    public setParameters(aParameters: any)
    {
        for (let key in aParameters) {
            this.parameters[key] = aParameters[key];
        }

        // Set specific values for screen framebuffer
        if (this.parameters.screen === true) {
            this.parameters.screen = true;
            this.parameters.createColorTexture = false;
            this.parameters.createDepthTexture = false;
            this.parameters.createDepthRenderBuffer = false;
        }
        // Default values for offscreen framebuffers
        else {
            this.parameters.screen = false;
        }
    }

    public getParameters(): any {
        return this.parameters;
    }

    private initialize(): void
    {
        this.validContent = false;

        if (this.renderable) {
            this.renderable.release();
        }

        // Create framebuffer by it's type
        if (this.parameters.screen) {
            this.renderable = new ScreenFrameBuffer(this);
        }
        else if (this.parameters.mrt) {
            this.renderable = new MRTFrameBuffer(this);
        }
        else {
            this.renderable = new OffscreenFrameBuffer(this);
        }

        this.renderable.initialize();
    }

    public bind(): void
    {
        this.renderable.bind();

        this._renderGraph.setActiveFramebuffer(this);
    }

    public unbind(aRenderPassTypeName?: string): void
    {
        this.renderable.unbind();

        this.validContent = true;

        // we resue the framebuffers across render passes to save memory so in order to checking if the framebuffer has
        // the same ocntent between two render passes, then we have to check not only if the camera has changed, but
        // also the type of the rendered pass as each type can render different data to the framebuffer e.g. ids, depth, etc.
        if (aRenderPassTypeName !== undefined) {
            this.renderPassTypeName = aRenderPassTypeName;
        }

        this._renderGraph.setActiveFramebuffer(null);
    }

    public hasValidContent(aPassTypeName: string): boolean
    {
        return this.validContent && (aPassTypeName === this.renderPassTypeName);
    }

    public invalidateContent(): void
    {
        this.validContent = false;
    }

    public _decodeFloatFromRGBA(aData: vec4|Uint8Array): number
    {
        // from http://stackoverflow.com/questions/18453302/how-do-you-pack-one-32bit-int-into-4-8bit-ints-in-glsl-webgl
        let enc = vec4.fromValues(1.0,255.0,65025.0,16581375.0);
        let dec = vec4.fromValues(1.0, 1.0 / enc[1], 1.0 / enc[2], 1.0 / enc[3]);

        return vec4.dot(<vec4>aData, dec) / 255.0;
    }

    public _decodeIntFromRGBA(aData: Uint8Array | Float32Array): number
    {
        return 16777216 * aData[0] + 65536 * aData[1] + 256 * aData[2] + aData[3];
    }

    /***
     * Get depth value from rendered data to framebuffer object from texture attachment. We have to count with scale when accessing
     * the texture data, but we need to return the result (picked point in model space) like there is no scale.
     *
     * @param aView view instance used to compute the returned point in model space
     * @param aXPositionInCanvas X position relative to canvas (ignore the view position)
     * @param aYPositionInCanvas Y position relative to canvas (ignore the view position)
     * @returns {Float32Array(3)} picked position of null
     */
    public readPoint(aView: View, aXPositionInCanvas: number, aYPositionInCanvas: number): vec3
    {
        let gl = this._context.getWebGLRenderingContext();

        // read pixel from the correct position according the scaled input position in cavas
        let data = this.renderable.getData(
            aXPositionInCanvas * this.parameters.scale,
            this.parameters.size[1] - (this.parameters.scale) - (aYPositionInCanvas * this.parameters.scale), // the (this.target_fbo.settings.scale) - aYPositionRelativeToViewPosition; is a scaled equivalent to (1 - notscaledY)
            1,
            1,
            gl.RGBA,
            gl.UNSIGNED_BYTE
        );

        let depthValue = this._decodeFloatFromRGBA(data);
        if (depthValue === 0) {
            return null;
        }

        let position = aView.getPosition();
        let size = aView.getSize();

        // get the picked point position in model space based on the canvas size and viewport defined by the view (the scale plays no role here)
        unProject(
            this._pgc.pickedPoint,
            aXPositionInCanvas,
            aView.getHTMLCanvas().height - 1.0 - aYPositionInCanvas,
            depthValue,
            aView.getCamera().getViewProjectionMatrix(),
            [position[0], position[1], size[0], size[1]]
        );

        return this._pgc.pickedPoint;
    }

    public readGeometryObjectUIDValue(aView: View, aXPositionInCanvas: number, aYPositionInCanvas: number): any
    {
        let scaledPositionInCanvas = vec2.fromValues(aXPositionInCanvas * this.parameters.scale, aYPositionInCanvas * this.parameters.scale);

        let gl = this._context.getWebGLRenderingContext();
        let pickingInfo: any = {};

        try {
            let x = scaledPositionInCanvas[0];
            let y = this.parameters.size[1] - (this.parameters.scale) - scaledPositionInCanvas[1];

            let data = this.renderable.getData(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE);

            let shapeUID = this._decodeIntFromRGBA(data);

            pickingInfo.shapeUID = shapeUID;
        }
        catch (se) {
            logException(se + " (cannot pick object id)");
            return null;
        }

        return pickingInfo;
    }

    public readPrimitiveIdValue(aView: View, aXPositionInCanvas: number, aYPositionInCanvas: number): any
    {
        let scaledPositionInCanvas = vec2.fromValues(aXPositionInCanvas * this.parameters.scale, aYPositionInCanvas * this.parameters.scale);

        let gl = this._context.getWebGLRenderingContext();
        let pickingInfo: any = {};

        try {
            let x = scaledPositionInCanvas[0];
            let y = this.parameters.size[1] - (this.parameters.scale) - scaledPositionInCanvas[1];

            let data = this.renderable.getData(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE);

            let primitiveID = this._decodeIntFromRGBA(data);

            pickingInfo.primitiveIndex = primitiveID;
        }
        catch (se) {
            logException(se + " (cannot pick primitive id)");
            return null;
        }

        return pickingInfo;
    }

    /**
     * Get RGBA information stored in texture
     * @param aView
     * @param aXPositionInCanvas
     * @param aYPositionInCanvas
     * @return {Uint8Array | Float32Array}
     */
    private readData(aView: View, aXPositionInCanvas: number, aYPositionInCanvas: number): Uint8Array | Float32Array
    {
        let scaledPositionInCanvas = vec2.fromValues(aXPositionInCanvas * this.parameters.scale, aYPositionInCanvas * this.parameters.scale);

        /** @type WebGLRenderingContext */
        let gl = this._context.getWebGLRenderingContext();
        let data = null;

        try {
            let x = scaledPositionInCanvas[0];
            let y = this.parameters.size[1] - (this.parameters.scale) - scaledPositionInCanvas[1];

            if (this.parameters.colorTexturePixelDataType === gl.UNSIGNED_BYTE) {
                data = new Uint8Array(4);
            } else {
                data = new Float32Array(4);
            }

            this.renderable.bind();
            gl.readPixels(x, y, 1, 1, gl.RGBA, this.parameters.colorTexturePixelDataType, data);
        }
        catch (se) {
            logException(se + " (cannot pick primitive id)");
        }

        return data;
    }

    /**
     * Get RGBA information stored in texture
     * @param aView
     * @param aXPositionInCanvas
     * @param aYPositionInCanvas
     * @return {number}
     */
    public readInt(aView: View, aXPositionInCanvas: number, aYPositionInCanvas: number): number
    {
        let data = this.readData(aView, aXPositionInCanvas, aYPositionInCanvas);
        if (!data) {
            return -1;
        }
        return this._decodeIntFromRGBA(data);
    }
}

